import React, { useState } from "react";
import { useRouter } from "next/router";
import { Modal } from "antd";
import { Hooks } from "@xt/xtbase";
import useIsDesktop from "@/hooks/useIsDesktop";
import couponGiftIcon from "@/assets/images/couponGift.png";
import VoucherCard from "@/components/Pages/Activity/Voucher/VoucherCard";
import {
  canShowVoucherUse,
  getVoucherNavigateButtonLabelId,
  navigateVoucherUse,
} from "@/components/Pages/Activity/Voucher/voucherUseJump";
import { canShowVoucherCashRedeem } from "@/components/Pages/Activity/Voucher/normalizeUserCoupon";
import VoucherCashRedeemModal from "@/components/Pages/Activity/Voucher/VoucherCashRedeemModal";
import styles from "./index.module.less";

const { useTranslation } = Hooks;

/**
 * 领券/送券成功弹窗
 * - variant="home"：首页注册送券 / 注册新人券每日提醒
 * - variant="redeem"：/activity/voucher 兑换领券成功（移动端沿用原样式，PC Spikex）
 */
export default function VoucherClaimModal({
  visible,
  coupon,
  onClose,
  titleId = "voucher-claim-modal-title",
  variant = "home",
  showNeverPrompt = false,
  neverPrompt = false,
  onNeverPromptChange,
  onCashRedeemSuccess,
}) {
  const t = useTranslation();
  const router = useRouter();
  const { isDesktop, isReady } = useIsDesktop();
  const [cashRedeemCoupon, setCashRedeemCoupon] = useState(null);

  const isRedeemVariant = variant === "redeem";
  const isRedeemPc = isRedeemVariant && isReady && isDesktop;
  const canCashRedeem = coupon ? canShowVoucherCashRedeem(coupon) : false;
  const canUseCoupon = coupon ? canShowVoucherUse(coupon, true) : false;

  const handlePrimaryAction = () => {
    if (canCashRedeem && coupon) {
      setCashRedeemCoupon(coupon);
      onClose({ neverPrompt });
      return;
    }
    if (canUseCoupon && coupon) {
      navigateVoucherUse(coupon, { router });
    }
    onClose({ neverPrompt });
  };

  const handleOverlayClose = () => {
    onClose({ neverPrompt });
  };

  const primaryLabel = canCashRedeem
    ? t("activity.voucherCashRedeem")
    : canUseCoupon && coupon
      ? t(getVoucherNavigateButtonLabelId(coupon)) ||
        (getVoucherNavigateButtonLabelId(coupon) === "activity.voucherTrade" ? "Trade" : "Use")
      : t("activity.voucherUseAicopy") || "Use";

  const titleText = isRedeemVariant
    ? t("activity.voucherModalCongrats") || "Congratulations!"
    : t("home.registerCouponModal.title");

  const successDesc =
    t("activity.voucherModalSuccessLine1") || "You have claimed this voucher successfully.";

  const cardBlock = coupon ? (
    <div className={styles.cardSlot}>
      <VoucherCard item={coupon} t={t} layout="modal" showAicopyUse={false} />
    </div>
  ) : null;

  const neverPromptBlock =
    showNeverPrompt && !isRedeemVariant ? (
      <label className={styles.neverPrompt}>
        <span className={styles.neverPromptCheckboxWrap}>
          <input
            type="checkbox"
            className={styles.neverPromptInput}
            checked={neverPrompt}
            onChange={(e) => onNeverPromptChange?.(e.target.checked)}
          />
          <span className={styles.neverPromptCheckboxVisual} aria-hidden />
        </span>
        <span className={styles.neverPromptText}>
          {t("home.unusedCouponModal.neverShow") || "Don't show again"}
        </span>
      </label>
    ) : null;

  const actionBlock = (buttonClassName) =>
    canCashRedeem || canUseCoupon ? (
      <button type="button" className={buttonClassName} onClick={handlePrimaryAction}>
        {primaryLabel}
      </button>
    ) : null;

  const giftHeaderBlock = (layout) => {
    const isPcLayout = layout === "pc";

    return (
      <div className={isPcLayout ? styles.redeemHeader : styles.header}>
        <img className={styles.icon} src={couponGiftIcon} alt="" />
        <h2 id={titleId} className={isPcLayout ? styles.redeemTitle : styles.title}>
          {titleText}
        </h2>
        {isPcLayout ? <p className={styles.redeemDesc}>{successDesc}</p> : null}
      </div>
    );
  };

  const mobileOverlay =
    visible && coupon ? (
      <div className={styles.overlay} role="presentation" onClick={handleOverlayClose}>
        <div
          className={styles.dialog}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={(e) => e.stopPropagation()}
        >
          {giftHeaderBlock("mobile")}
          {cardBlock}
          {neverPromptBlock}
          {actionBlock(styles.okBtn)}
        </div>
      </div>
    ) : null;

  return (
    <>
      {isRedeemPc ? (
        <Modal
          open={visible && !!coupon}
          footer={null}
          closable={false}
          maskClosable
          centered
          onCancel={handleOverlayClose}
          width={400}
          destroyOnClose
          className={styles.redeemModal}
          wrapClassName={styles.redeemModalWrap}
          zIndex={1300}
        >
          <div className={styles.redeemBody}>
            {giftHeaderBlock("pc")}
            {cardBlock}
            {actionBlock(styles.redeemBtn)}
          </div>
        </Modal>
      ) : (
        mobileOverlay
      )}

      <VoucherCashRedeemModal
        open={!!cashRedeemCoupon}
        coupon={cashRedeemCoupon}
        onClose={() => setCashRedeemCoupon(null)}
        onSuccess={(redeemed) => {
          onCashRedeemSuccess?.(redeemed);
          setCashRedeemCoupon(null);
        }}
      />
    </>
  );
}
